// src/components/profile/ProfileCard.tsx
import React, { useRef } from 'react';
import { Edit, Camera } from 'lucide-react';
import { ProfileRecord } from '../../pages/Profile'; // Adjust this path if your Profile.tsx is elsewhere

interface ProfileCardProps {
  profileData: ProfileRecord | null;
  getLabel: (group: string, value: string) => string;
  onEdit: () => void;
  onPictureChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profileData, getLabel, onEdit, onPictureChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const triggerFileSelect = () => fileInputRef.current?.click();

  return (
    <div className="bg-white rounded-2xl p-2 sm:p-8 shadow-lg border border-gray-100 h-full relative overflow-hidden">
      <div className="text-center relative z-10">
        <div className="relative w-32 h-32 sm:w-48 sm:h-48 mx-auto mb-4 sm:mb-6 group">
          <img
            src={profileData?.profilePicture || '/default-avatar.png'}
            alt="Profile"
            className="w-full h-full rounded-full border-2 sm:border-4 border-white object-cover shadow-md"
          />
          <div
            onClick={triggerFileSelect}
            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300"
          >
            <Camera className="w-8 h-8 sm:w-12 sm:h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          <input type="file" ref={fileInputRef} onChange={onPictureChange} accept="image/*" className="hidden" />
        </div>
        <h2 className="text-[12px] sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{profileData?.name || 'Your Name'}</h2>
        <button
          onClick={onEdit}
          disabled={!profileData}
          className="w-full bg-blue-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center shadow-md disabled:opacity-50"
        >
          <Edit className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-2" />
          <span className="text-[11px] sm:text-base">Update Profile</span>
        </button>
      </div>
      {profileData && (
        <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3 relative z-10">
          <h3 className="text-[12px] sm:text-xl font-semibold text-gray-900 border-b border-gray-200 pb-1 sm:pb-2">Information</h3>
          <div className="space-y-1 sm:space-y-2 text-[11px] sm:text-base">
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Profile Type:</span> <span className="text-gray-900">{getLabel('profileType', profileData.profileType)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Date of Birth:</span> <span className="text-gray-900">{profileData.dob || 'N/A'}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Gender:</span> <span className="text-gray-900">{getLabel('gender', profileData.gender)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Marital Status:</span> <span className="text-gray-900">{getLabel('maritalStatus', profileData.maritalStatus)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Location:</span> <span className="text-gray-900 text-right">{getLabel(`cities-${profileData.country}-${profileData.state}`, profileData.city)}, {getLabel(`states-${profileData.country}`, profileData.state)}, {getLabel('countries', profileData.country)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Currency:</span> <span className="text-gray-900">{getLabel('currencies', profileData.currency)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Employment:</span> <span className="text-gray-900">{getLabel('employmentStatus', profileData.employmentStatus)}</span> </div>
            <div className="flex justify-between"> <span className="text-gray-600 font-medium">Retirement Age:</span> <span className="text-gray-900">{profileData.retirementAge ? `${profileData.retirementAge} Years` : 'N/A'}</span> </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCard;